import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
    getAllUserVisit, downloadUserMetrics
} from "../../Services/user_metrics";
import { formatDate } from "../../utils/date";
import { Button, Flex, DatePicker, Select, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import UserMetricsAction from "./UserMetricsAction";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { fetchUserForSearch } from "../../Services/user_question_history";
const { RangePicker } = DatePicker;

const ellipsisRender = (width) => (text) => (
  <Tooltip title={text}>
    <div
      style={{
        maxWidth: width,               // must match the column width
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text ?? "-"}
    </div>
  </Tooltip>
);

const columns = (showViewModal) => [
    {
        title: "#ID",
        dataIndex: "session_ids",
        key: "session_ids",
        width: 40,
        ellipsis: { showTitle: false },
        render: ellipsisRender(60),
    },
    {
        title: "User Name",
        dataIndex: "user_name",
        key: "user_name",
    },
    {
        title: "Email",
        dataIndex: "user_email",
        key: "user_email",
    },
    {
        title: "Time of Visit",
        dataIndex: "created_at",
        key: "created_at",
        render: (text, record) =>
            record?.created_at ? formatDate(record.created_at) : "-",
    },
    {
        title: "Last Active at",
        dataIndex: "last_active_at",
        key: "last_active_at",
        render: (text, record) =>
            record?.last_active_at ? formatDate(record.last_active_at) : "-",
    },
    {
        title: "Active Duration",
        dataIndex: "duration_minutes",
        key: "duration_minutes",
    },
    {
        title: "Exit Page",
        dataIndex: "last_page_visit",
        key: "last_page_visit",
    },
    {
        title: "User IP",
        dataIndex: "ip_address",
        key: "ip_address",
    },
    {
        title: "Platform",
        dataIndex: "plat_form",
        key: "plat_form",
    },
    {
        title: "Device",
        dataIndex: "device",
        key: "device",
        render: (text, record) =>{
            const platformOS = [record.plat_form, record.device_os]
            .filter(Boolean) // removes null/undefined/empty
            .join("-");      // join with dash if both exist

            const parts = [
                platformOS || null, // add combined platform+OS
                record.device || null
            ].filter(Boolean); // remove null/empty again

            return parts.length ? parts.join(" ") : "-";
        }
    },
    {
        title: "User Location (Approx)",
        dataIndex: "country",
        key: "country",
        render: (text, record) =>{
            const parts = [
                record.city || null,
                record.state || null,
                record.country || null
            ].filter(Boolean); // remove null/undefined/empty

            return parts.length ? parts.join(", ") : "-";
        }
    },
    {
        title: "Actions",
        key: "actions",
        fixed: 'right',
        render: (_, record) => (
            <Flex gap="small">
                <Button type="link" icon={<EyeOutlined />} onClick={() => showViewModal(record)} > {record.action_count}</Button>
            </Flex>
        ),
    },
];

function UserMetricsVisit() {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedUserId, setSelectedUserId] = useState([]);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [dateRange, setDateRange] = useState({
            start_date: null,
            end_date: null,
        });

    const showViewModal = (record) => {
        setViewData(record);
        setViewModalVisible(true);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedUserId, dateRange]);

    const skip = (currentPage - 1) * pageSize;

    const { data, isLoading } = useQuery({
        queryKey: ["user_metrics_visit", currentPage, pageSize, selectedUserId, dateRange, selectedVisit],
        queryFn: () =>
            getAllUserVisit({
                skip,
                limit: pageSize,
                user_id: selectedUserId,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                visit_type: selectedVisit
            }),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleDateChange = (dates) => {
        setCurrentPage(1);
        if (dates) {
            setDateRange({
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
            });
        } else {
            setDateRange({
                start_date: null,
                end_date: null,
            });
        }
    };

    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
          return await downloadUserMetrics({
                user_id: selectedUserId,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                visit_type: selectedVisit
          });
        },
      });

    return (
        <div>
            <Flex justify="space-between">
                <h3>User Metrics Visit</h3>
                <Button type="primary" onClick={triggerDownload} loading={isDownloading}>Export</Button>
            </Flex>
            <Flex gap="10px">
                {/* <CustomFilterSelect
                    label="User"
                    fetchOptions={fetchUsers}
                    filterKey="user"
                    onChange={(key, value) => {setSelectedUserId(value); setCurrentPage(1);}}
                    renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                    className="custom-user-select"
                /> */}
                <CustomFilterSelect
                    label="Users"
                    fetchOptions={fetchUserForSearch}
                    filterKey="user"
                    onChange={(key, value) => setSelectedUserId(value)}
                    renderOption={(option) =>
                        `#${option.value} ${option.label} (${option.email})`
                    }
                    className="custom-user-select"
                    mode="multiple"
                    value={selectedUserId}
                    copyPaste={true}
                />
                <div >
                    <RangePicker onChange={handleDateChange} disabledDate={(current) => current && current.toDate() > new Date()} />
                </div>
                <Select
                    style={{ width: 180 }}
                    allowClear
                    options={[{ value: 'guest', label: 'Guest User' }, { value: 'with_user_id', label: 'Registered Users' }]}
                    placeholder="Select User Type"
                    onChange={(key, value) => {setSelectedVisit(value?.value); setCurrentPage(1);}}
                />
            </Flex>
            <br />
            <CustomTable
                columns={columns(showViewModal)}
                data={data?.data || []}
                loading={isLoading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight={"calc(90vh - 180px)"}
                scroll={{ x: 'max-content',}}
            />

            <UserMetricsAction
                visible={viewModalVisible}
                onClose={() => setViewModalVisible(false)}
                data={viewData}
            />
        </div>
    );
}

export default UserMetricsVisit;
